import { api } from './client';

/**
 * What is offered at the address on screen, as ops has mapped it.
 *
 * Not to be confused with `services/availability.ts`, which is about TIME -
 * which slots on a day are full. This is about PLACE - which services reach
 * the address at all.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NOT THE CATALOGUE
 * ---------------------------------------------------------------------------
 * `/catalog/categories` is the whole platform, answered for nowhere. A city is
 * divided into areas, and ops assigns each service to the areas it is actually
 * run in - so a customer in a covered city can still be standing somewhere a
 * given service has not reached yet. The catalogue cannot say that; it does
 * not know where anybody is. `/geo/catalog` answers the same list for one pin.
 *
 * ---------------------------------------------------------------------------
 * IT FOLLOWS THE CITY GATE, AND THE SERVER DECIDES THAT
 * ---------------------------------------------------------------------------
 * Areas only refuse a booking while the city's
 * `geo.enforceAreaServiceAvailability` is on. With it off, a booking goes
 * through wherever the pin is, and greying a service out would tell a customer
 * "coming soon" about something they could book right now. The server reads
 * that gate and reports it as `enforced`; this file never second-guesses it.
 *
 * ---------------------------------------------------------------------------
 * IT FAILS OPEN
 * ---------------------------------------------------------------------------
 * Any failure - offline, a timeout, an older backend without `enforced` - reads
 * as "nothing is unavailable". Greying services out on a network blip would
 * turn away customers the platform can serve, and it is not the last line of
 * defence anyway: booking re-checks the area server-side and refuses there.
 */

/** The unavailable parts of the catalogue at one address. */
export type AreaAvailability = {
  /** Whether the city gate is on. Off means nothing below can be non-empty. */
  enforced: boolean;
  /** Service keys (the platform's service ids) not offered here. */
  services: ReadonlySet<string>;
  /** Category keys every one of whose services is unavailable here. */
  categories: ReadonlySet<string>;
};

const EMPTY: ReadonlySet<string> = new Set<string>();

/** Nothing unavailable - the state before an answer, and after a failure. */
export const OPEN_AVAILABILITY: AreaAvailability = {
  enforced: false,
  services: EMPTY,
  categories: EMPTY,
};

type LocationCatalogResponse = {
  enforced?: boolean;
  /** False when the gate is on and the pin is outside every served area. */
  serviceable?: boolean;
  services?: { id: string; isAvailable: boolean }[];
};

/**
 * The services not offered at one pin.
 *
 * `cityId` is the city the app has already matched the pin to. The server only
 * reads it for a pin that falls between areas - an area already knows its own
 * city - to decide which city's gate applies.
 *
 * Never rejects. See the note on failing open above.
 */
export async function fetchUnavailableServices(
  pin: { lat: number; lng: number },
  cityId: string | undefined,
  signal?: AbortSignal,
): Promise<{
  enforced: boolean;
  services: ReadonlySet<string>;
  /**
   * The pin is in no area ops has opened, and the city's gate is on - so
   * nothing can be booked here. Drives Home's soft "not in your area yet".
   * False on any failure, like everything else here.
   */
  outsideAreas: boolean;
}> {
  try {
    const response = await api.get<LocationCatalogResponse>('/geo/catalog', {
      anonymous: true,
      signal,
      query: { lat: pin.lat, lng: pin.lng, cityId },
    });

    /* `!== true`, not a falsy check: a backend that predates the field sends
       no `enforced` at all, and that has to read as "not enforced". */
    if (response?.enforced !== true) {
      return { enforced: false, services: EMPTY, outsideAreas: false };
    }

    return {
      enforced: true,
      outsideAreas: response.serviceable === false,
      services: new Set(
        (response.services ?? [])
          .filter(service => !service.isAvailable)
          .map(service => service.id),
      ),
    };
  } catch {
    return { enforced: false, services: EMPTY, outsideAreas: false };
  }
}

/**
 * The categories to grey out: those EVERY one of whose services is unavailable.
 *
 * A category with even one service offered here stays a normal tile, and the
 * missing ones are greyed inside it - greying the whole trade would hide the
 * one thing the customer could actually book.
 *
 * A category with no services at all is left alone. "Coming soon" is a promise
 * about something that exists, and an empty shelf is a catalogue question, not
 * a question about where the customer lives.
 */
export function unavailableCategories(
  categoryKeys: readonly string[],
  serviceKeysIn: (categoryKey: string) => readonly string[],
  unavailable: ReadonlySet<string>,
): ReadonlySet<string> {
  if (unavailable.size === 0) {
    return EMPTY;
  }

  const out = new Set<string>();
  for (const key of categoryKeys) {
    const keys = serviceKeysIn(key);
    if (
      keys.length > 0 &&
      keys.every(serviceKey => unavailable.has(serviceKey))
    ) {
      out.add(key);
    }
  }
  return out;
}

/*
 * ---------------------------------------------------------------------------
 * THE STORE
 * ---------------------------------------------------------------------------
 * Module-level rather than a React context, and the reason is the screen that
 * owns the answer. `HomeScreen` returns early for every full-screen view it
 * draws - the service sheet, a category's page, search, a chat - and a context
 * provider around its main tree would not reach any of them. A category's page
 * is exactly where a customer meets a greyed-out service. The same shape as
 * `appConfig()`: one writer, any number of readers, read at render.
 */

let current: AreaAvailability = OPEN_AVAILABILITY;
const listeners = new Set<() => void>();

function sameSet(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a === b) {
    return true;
  }
  if (a.size !== b.size) {
    return false;
  }
  for (const value of a) {
    if (!b.has(value)) {
      return false;
    }
  }
  return true;
}

/**
 * Replaces the answer and tells every reader.
 *
 * An answer identical to the one held is dropped without notifying: the writer
 * recomputes on every catalogue change, and re-rendering every tile on the
 * screen for a result that did not move is work nobody can see.
 */
export function setAreaAvailability(next: AreaAvailability): void {
  if (
    current.enforced === next.enforced &&
    sameSet(current.services, next.services) &&
    sameSet(current.categories, next.categories)
  ) {
    return;
  }
  current = next;
  listeners.forEach(listener => listener());
}

/** The answer held right now. Stable between writes, as a snapshot must be. */
export function getAreaAvailability(): AreaAvailability {
  return current;
}

export function subscribeAreaAvailability(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
